import React, {FunctionComponent, useEffect, useState} from "react";
import {RootState} from "../../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../../Config/Hooks";
import {ComparisonSkid, getEntities} from "./SkidComparisonChartReducer";
import {format} from 'date-fns';
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {
    AChartX,
    Period,
    Skeleton,
    XAxis,
    YAxis, PeriodSelectorGroupProps
} from "@atiautomacao/ati-ui-library"
import {checkDiffDays} from "../../../../Utils/DataUitils";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {useTheme} from "@mui/material";
import { getChartXOptions } from "../../../../Utils/ChartXOption";


interface SeriesOption {
    name: string;
    type: string;
    unity: string;
    yAxisIndex?: number | undefined;
    data: any[];
    date?: any;
    tooltip?: any;
}

interface SkidComparisonChartProps {
    period?: Period;
    periodIntervalGroup? : PeriodSelectorGroupProps;
    powerPlant: any;
}


const SkidComparisonChart: FunctionComponent<SkidComparisonChartProps> = (props: SkidComparisonChartProps) => {
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const comparisonSkid: Array<ComparisonSkid> = useAppSelector((state: RootState) => state.skidComparisonChart.entities);
    const { loading } = useAppSelector((state: RootState) => state.skidComparisonChart);
    const dispatch = useAppDispatch();
    const [showGraph, setShowGraph] = useState(false);
    const theme = useTheme();
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    // Setting range start period
    const initialPeriod: Period = {
        groupBy: "day",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };

    const max = (values: Array<number | null>) => {
        if (!Array.isArray(values)) return 0;
        const numeric = values.filter((v): v is number => v != null && !Number.isNaN(v));
        if (numeric.length === 0) return 0;
        return truncateNumber(Math.max(...numeric) * 1.15) ?? 0;
    }

    const valueFormatter = (value:any,fromDateTime:any, toDateTime:any) => {
        let data = formatCaption(fromDateTime, toDateTime)
        return  `${value} MWh ${data}`;
    }

    const [period, setPeriod] = useState<Period>(props.period ? props.period : initialPeriod);
    const [series, setSeries] = useState<Array<SeriesOption>>(new Array<SeriesOption>);
    const [xAxis, setXAxis] = useState<XAxis>();
    const [yAxis, setYAxis] = useState<Array<YAxis>>(new Array<YAxis>);

    function formatCaption(fromDateTime: Date, toDateTime: Date): string{

        let dateInterval;
        if (period.groupBy && (period.groupBy.valueOf() === "none" || period.groupBy.valueOf() === "week" || period.groupBy.valueOf() === "month")) {
            dateInterval = format(fromDateTime, 'dd/MM') + ' a ' + format(toDateTime, 'dd/MM')
        } else {
            dateInterval = format(fromDateTime, 'HH:mm' ) + ' a ' + format(toDateTime, 'HH:mm')
        }

        return dateInterval
    }

    const newNameGap = () => {
        if (period.groupBy) {
            switch (period.groupBy) {
                case "day":
                    return 40;
                case "week":
                    return 46;
                case "month":
                    return 50;
                default:
                    return 40;
            }
        }
        return 40;
    };


    function dateByPeriod(): string{

        let fromDateTime = new Date();
        let toDateTime = new Date();
        let captionData :  string ;

        if(period.fromDateTime) {
            fromDateTime = period.fromDateTime
        }
        if(period.toDateTime) {
            toDateTime = period.toDateTime
        }

        captionData = (formatCaption(fromDateTime, toDateTime))
        return captionData
    }

    function findSkidComparisonByPeriod() {
        let datePeriod: any = dateByPeriod()

        setShowGraph(comparisonSkid.length > 0)
        setXAxis({data: Array.isArray(comparisonSkid) ? comparisonSkid.map(item => item.skidName) : []});

        setSeries([
                {
                    name: 'Energia Gerada',
                    data: Array.isArray(comparisonSkid) ? comparisonSkid.map(item => truncateNumber(item.value, 2)) : [],
                    type: 'bar',
                    unity: 'MWh',
                    date: datePeriod,
                    tooltip:{
                        valueFormatter: (value: any) => valueFormatter(value, period.fromDateTime, period.toDateTime)
                    }
                },
                {
                    name: 'Energia Esperada',
                    data: Array.isArray(comparisonSkid) ? comparisonSkid.map(item => truncateNumber(item.valueExpectedEnergy, 2)) : [],
                    type: 'bar',
                    unity: 'MWh',
                    date: datePeriod,
                    tooltip:{
                        valueFormatter: (value: any) => valueFormatter(value, period.fromDateTime, period.toDateTime)
                    }
                },
                {
                    name: 'Energia Esperada Corrigida',
                    data: Array.isArray(comparisonSkid) ? comparisonSkid.map(item => truncateNumber(item.valueExpectedEnergyCorrectedByIrradiation, 2)) : [],
                    type: 'bar',
                    unity: 'MWh',
                    date: datePeriod,
                    tooltip:{
                        valueFormatter: (value: any) => valueFormatter(value, period.fromDateTime, period.toDateTime)
                    }
                }
            ]
        );

        const maxAxisY = max(comparisonSkid.map(item => item.value));
        const maxAxisYExpectedEnergy = max(comparisonSkid.map(item => item.valueExpectedEnergy));
        const maxAxisYExpectedEnergyCorrectedByIrradiation = max(comparisonSkid.map(item => item.valueExpectedEnergyCorrectedByIrradiation));

        const globalMax = Math.max(
            maxAxisY,
            maxAxisYExpectedEnergy,
            maxAxisYExpectedEnergyCorrectedByIrradiation
        );

        setYAxis([
            {
                name: 'MWh',
                max: globalMax > 1 ? globalMax : 1,
                min: 0,
                interval: globalMax > 1 ? truncateNumber(globalMax / 6) : 0.20,
                nameGap: newNameGap(),
                nameLocation: "middle"
            }
        ])
    }

    useEffect(() => {
        if(props.period){
            setPeriod(props.period);
        }
    }, [props.period]);

    useEffect(() => {
        setSeries([])
    }, [props.powerPlant]);


    const fetchData = () => {
        if (checkDiffDays(period.toDateTime, period.fromDateTime) > 0) {
            dispatch(getEntities({ period, idPowerPlant: props.powerPlant.id }));
        }
    };

    useEffect(() => {
        fetchData();
    }, [period, dispatch, props.powerPlant]);

    useInterval(() => {
        fetchData();
    }, userConfig.mainUpdateInterval); // 1 minute

    useEffect(() => {
        dateByPeriod();
        if (comparisonSkid) findSkidComparisonByPeriod();
    }, [comparisonSkid]);

    // @ts-ignore
    const option: AChartXProps['option'] = {
        ...getChartXOptions({name:"energia-gerada",date:new Date(new Date())}),
        color: ['#5470C6', '#91CC75', '#f1f41e', 'yellow'],
        legend: {
            type: 'scroll',
            show: true,
            orient: 'horizontal', // Alterado para orientação horizontal
            bottom: 2, // Posiciona a legenda na parte inferior
            left: 'center', // Centraliza a legenda horizontalmente
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {
                opacity: 1
            },
            itemStyle: {
                opacity: 1
            },
            data: ['Energia Gerada', 'Energia Esperada', 'Energia Esperada Corrigida']
        },
        xAxis: xAxis,
        yAxis: yAxis,
        series: series
    };

    return (
        <>
            {
                loading ?
                    <Skeleton animation="wave" height={350} variant="rounded" width={"100%"}/>
                    :
                    series && xAxis && yAxis && showGraph ?
                        <AChartX
                            key={`chart-power-energy-skid-comparison-${openSubMenu}`}
                            option={option}
                            height={350}
                            width={"100%"}
                            loading={false}
                            theme={theme.palette.mode}
                        />
                        :
                        <DataNotFound boxStyle={{height: 350, width: '100%'}}/>
            }
        </>
    );
}
export default SkidComparisonChart;
